import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import { MessagesSquare, Send, UserPlus, Hand, Bot, User, Loader2, X, Link2, Check, Ban, Radio, Sparkles, DollarSign, Wand2, ArrowUpRight, ChevronDown } from "lucide-react";

function LeadSource({ utm, testid }) {
  if (!utm || (!utm.source && !utm.campaign && !utm.medium)) return null;
  const chips = [];
  if (utm.source) chips.push(utm.source);
  if (utm.campaign) chips.push(utm.campaign);
  if (utm.medium) chips.push(utm.medium);
  return (
    <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-testid={testid}>
      <Radio className="w-3 h-3 text-emerald-400 shrink-0" />
      {chips.map((v, i) => (
        <span key={i} className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] tracking-wide">{v}</span>
      ))}
    </div>
  );
}

const NEG_META = {
  interested: { icon: Sparkles, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "Interested" },
  price: { icon: DollarSign, color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/30", label: "Price question" },
  feature: { icon: Wand2, color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/30", label: "Feature request" },
  not_interested: { icon: Ban, color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/30", label: "Not interested" },
  other: { icon: ArrowUpRight, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", label: "Unclear" },
};

function NegotiatorTimeline({ events, lastIntent }) {
  const [open, setOpen] = useState(true);
  if (!events || !events.length) return null;
  return (
    <div className="mx-5 mt-3 rounded-md border border-slate-800 bg-slate-900/40" data-testid="negotiator-timeline">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2" data-testid="negotiator-timeline-toggle">
        <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400">
          <Bot className="w-3.5 h-3.5 text-cyan-400" /> Auto-negotiator · {events.length} step{events.length !== 1 ? "s" : ""}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {events.map((e, i) => {
            const meta = NEG_META[e.intent] || NEG_META.other;
            const Icon = meta.icon;
            return (
              <div key={i} data-testid={`neg-event-${i}`} className="flex items-start gap-2">
                <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center border ${meta.bg} shrink-0`}><Icon className={`w-3 h-3 ${meta.color}`} /></div>
                <div className="min-w-0">
                  <div className="text-[12px] text-slate-200"><span className={`font-semibold ${meta.color}`}>{meta.label}</span> · {e.note}</div>
                  <div className="text-[10px] font-mono text-slate-600">confidence {Math.round((e.confidence || 0) * 100)}%{e.ts ? ` · ${new Date(e.ts).toLocaleString()}` : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function Inbox() {
  const { user } = useOutletContext() || {};
  const isAdmin = user?.role === "admin";
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [filter, setFilter] = useState("all");
  const scrollRef = useRef(null);

  const loadConvos = () => api.get("/conversations").then((r) => setConvos(r.data)).catch(() => {});
  const loadDetail = (id) => api.get(`/conversations/${id}`).then((r) => setDetail(r.data)).catch(() => {});

  useEffect(() => { loadConvos(); const i = setInterval(loadConvos, 12000); return () => clearInterval(i); }, []);
  useEffect(() => { if (active) loadDetail(active); }, [active]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [detail]);
  useRealtime((msg) => {
    loadConvos();
    if (active && (!msg || msg.type === "chat" || msg.type === "event")) loadDetail(active);
  });

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !detail) return;
    setSending(true);
    const isHuman = detail.status === "human";
    try {
      const endpoint = isHuman ? `/conversations/${active}/human-reply` : `/conversations/${active}/message`;
      const { data } = await api.post(endpoint, { text });
      setText("");
      setDetail({ ...detail, messages: data.messages });
      loadConvos();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const handoff = async (mode) => {
    try {
      await api.post(`/conversations/${active}/handoff`, { mode });
      toast[mode === "human" ? "warning" : "success"](mode === "human" ? "You have taken over — AI paused" : "Returned to AI agent");
      loadDetail(active); loadConvos();
    } catch { toast.error("Handoff failed"); }
  };

  const resolveLink = async (approve) => {
    setResolving(true);
    try {
      const { data } = await api.post(`/conversations/${active}/payment-link/resolve`, { approve });
      setDetail({ ...detail, messages: data.messages, pending_payment_link: undefined });
      toast[approve ? "success" : "message"](approve ? "Payment link sent to the lead" : "Proposal rejected");
      loadDetail(active); loadConvos();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setResolving(false); }
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Live Customer Inbox & HITL Triage</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Customer Inbox</h1>
        </div>
        <button data-testid="new-customer-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors">
          <UserPlus className="w-4 h-4" /> New Customer
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 h-[calc(100vh-13rem)]">
        {/* Conversation list */}
        <div className="hud-card rounded-lg p-3 overflow-y-auto" data-testid="conversation-list">
          {(() => {
            const needsHuman = (c) => c.needs_human || c.status === "unassigned";
            const nhCount = convos.filter(needsHuman).length;
            const filtered = filter === "needs_human" ? convos.filter(needsHuman) : convos;
            return (
              <>
                <div className="flex gap-1.5 mb-2 sticky top-0" data-testid="inbox-filter">
                  <button data-testid="filter-all" onClick={() => setFilter("all")}
                    className={`flex-1 rounded-md px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider border transition-colors ${filter === "all" ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>
                    All ({convos.length})
                  </button>
                  <button data-testid="filter-needs-human" onClick={() => setFilter("needs_human")}
                    className={`flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider border transition-colors ${filter === "needs_human" ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>
                    <Hand className="w-3 h-3" /> Needs Human ({nhCount})
                  </button>
                </div>
                {filtered.length === 0 && <div className="text-xs text-slate-600 font-mono p-4 text-center">{filter === "needs_human" ? "Nothing waiting on a human. 🎉" : "No conversations. Add a customer to begin."}</div>}
                {filtered.map((c) => (
                  <button key={c.id} data-testid={`convo-${c.id}`} onClick={() => setActive(c.id)}
                    className={`w-full text-left rounded-md p-3 mb-1.5 border transition-colors ${active === c.id ? "border-cyan-500/40 bg-cyan-500/10" : needsHuman(c) ? "border-amber-500/25 hover:bg-slate-800/50" : "border-transparent hover:bg-slate-800/50"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm truncate">{c.customer_name}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-1 truncate">{c.agent_name ? `owned by ${c.agent_name}` : "unassigned"}</div>
                    {needsHuman(c) && <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-amber-400"><Hand className="w-2.5 h-2.5" /> handed to you</div>}
                    <LeadSource utm={c.utm} testid={`convo-source-${c.id}`} />
                  </button>
                ))}
              </>
            );
          })()}
        </div>

        {/* Chat pane */}
        <div className="lg:col-span-2 hud-card rounded-lg flex flex-col">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-slate-600 font-mono text-sm">
              <div className="text-center"><MessagesSquare className="w-10 h-10 mx-auto mb-2" /> Select a conversation</div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-sm">{detail.customer_name}</div>
                  <div className="text-[11px] font-mono text-slate-500">
                    {detail.status === "human" ? "Human operator in control" : detail.agent_name ? `AI agent: ${detail.agent_name} (ownership locked)` : "unassigned"}
                  </div>
                  <LeadSource utm={detail.utm} testid="detail-lead-source" />
                </div>
                <div className="flex gap-2">
                  {detail.agent_id && (
                    <button data-testid="propose-link-btn" onClick={() => setShowLink(true)} disabled={!!detail.pending_payment_link}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 font-mono text-[10px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors disabled:opacity-40" title="Have the agent propose a payment link (needs approval to send)">
                      <Link2 className="w-3 h-3" /> Payment Link
                    </button>
                  )}
                  {detail.status !== "human"
                    ? <button data-testid="takeover-btn" onClick={() => handoff("human")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase tracking-wider hover:bg-amber-500/20 transition-colors"><Hand className="w-3 h-3" /> Human Intervene</button>
                    : <button data-testid="return-ai-btn" onClick={() => handoff("ai")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 font-mono text-[10px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors"><Bot className="w-3 h-3" /> Return to AI</button>}
                </div>
              </div>

              <NegotiatorTimeline events={detail.negotiator_events} lastIntent={detail.last_intent} />

              {detail.pending_payment_link && (
                <div className="mx-5 mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3" data-testid="pending-link-banner">
                  <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-amber-400">
                    <Link2 className="w-3.5 h-3.5" /> Payment link awaiting approval
                  </div>
                  <p className="mt-1.5 text-[12px] text-slate-300">
                    {detail.pending_payment_link.product_name} — <span className="font-mono text-emerald-400">${Number(detail.pending_payment_link.amount).toLocaleString()}</span>
                    <span className="text-slate-500"> · proposed by {detail.pending_payment_link.proposed_by}</span>
                  </p>
                  {isAdmin ? (
                    <div className="mt-2.5 flex gap-2">
                      <button data-testid="approve-link-btn" onClick={() => resolveLink(true)} disabled={resolving}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/25 transition-colors disabled:opacity-60">
                        {resolving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve &amp; Send
                      </button>
                      <button data-testid="reject-link-btn" onClick={() => resolveLink(false)} disabled={resolving}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 font-mono text-[10px] uppercase tracking-wider hover:bg-red-500/25 transition-colors disabled:opacity-60">
                        <Ban className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 text-[10px] font-mono text-slate-500">An admin must approve before this link is sent.</div>
                  )}
                </div>
              )}

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages">
                {detail.messages.map((m) => <Bubble key={m.id} m={m} />)}
                {sending && <div className="text-xs text-slate-500 font-mono flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> agent thinking…</div>}
              </div>

              <form onSubmit={send} className="p-3 border-t border-slate-800 flex gap-2">
                <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={detail.status === "human" ? "Reply as human operator…" : "Message as customer (AI agent will respond)…"}
                  className="flex-1 px-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
                <button data-testid="chat-send-btn" type="submit" disabled={sending} className="px-4 rounded-md bg-cyan-500 text-[#080B11] hover:bg-cyan-400 transition-colors disabled:opacity-60">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {showNew && <NewCustomer onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); loadConvos(); }} />}
      {showLink && detail && <ProposeLinkModal conv={detail} onClose={() => setShowLink(false)} onDone={() => { setShowLink(false); loadDetail(active); loadConvos(); }} />}
    </div>
  );
}

function ProposeLinkModal({ conv, onClose, onDone }) {
  const [f, setF] = useState({ product_name: "", amount: 149 });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/conversations/${conv.id}/propose-payment-link`, { product_name: f.product_name, amount: Number(f.amount) });
      toast.success("Payment link proposed — awaiting admin approval to send");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md hud-card rounded-xl p-6 bg-[#0E131F]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Propose Payment Link</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-400 mb-4">{conv.agent_name} will offer <span className="text-slate-200">{conv.customer_name}</span> a payment link. An admin approves before it's sent — revenue is attributed to {conv.agent_name} on payment.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Product / Service</label>
            <input data-testid="link-product-input" value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} required placeholder="Landing Page Bundle"
              className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
          </div>
          <div>
            <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">Amount ($)</label>
            <input data-testid="link-amount-input" type="number" min="1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required
              className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
          </div>
          <button type="submit" data-testid="link-submit-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Propose Link for Approval"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Bubble({ m }) {
  if (m.sender === "system") return <div className="text-center text-[10px] font-mono text-slate-600 uppercase tracking-wider py-1">— {m.text} —</div>;
  const isCustomer = m.sender === "customer";
  const Icon = m.sender === "human" ? Hand : m.sender === "agent" ? Bot : User;
  return (
    <div className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[75%] rounded-lg px-3.5 py-2.5 border ${isCustomer ? "bg-slate-800/60 border-slate-700" : m.sender === "human" ? "bg-amber-500/10 border-amber-500/30" : "bg-cyan-500/10 border-cyan-500/30"}`}>
        <div className="flex items-center gap-1.5 mb-1">
          <Icon className={`w-3 h-3 ${isCustomer ? "text-slate-400" : m.sender === "human" ? "text-amber-400" : "text-cyan-400"}`} />
          <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{m.sender_name}</span>
        </div>
        <div className="text-sm text-slate-100 whitespace-pre-wrap break-words">
          {m.text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
            /^https?:\/\//.test(part)
              ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline break-all hover:text-cyan-300">{part}</a>
              : part
          )}
        </div>
      </div>
    </div>
  );
}

function NewCustomer({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", intent: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/customers", { ...f, intent: f.intent || "general inquiry" });
      toast.success("Customer added · conversation opened & AI agent assigned");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md hud-card rounded-xl p-6 bg-[#0E131F]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg">New Customer</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {[["Name", "name"], ["Email", "email"], ["Intent / Inquiry", "intent"]].map(([label, key]) => (
            <div key={key}>
              <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">{label}</label>
              <input data-testid={`customer-${key}-input`} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} required={key !== "intent"}
                placeholder={key === "intent" ? "wants a landing page for a coffee brand" : ""}
                className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
            </div>
          ))}
          <button type="submit" data-testid="customer-submit-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Customer"}
          </button>
        </form>
      </div>
    </div>
  );
}
